import { FC, useState } from 'react';

import dayjs from 'dayjs';
import { AtSign, Calendar, Crown, Drama, Fingerprint, Globe, Mail, MapPin, ShieldBan } from 'lucide-react';

import Chip from '@mui/material/Chip';

import css from './ProfileHeader.module.css';

import { Card } from 'v2/components/card/Card';
import { UserAvatar } from 'v2/components/userAvatar/UserAvatar';
import { LONG_DASH, geoToUIMap } from 'v2/constants';
import { UserModel } from 'v2/types/UserModel';
import { UserFingerprintDto, UserStatisticDto } from 'v2/types/UserProfileModel';

interface ProfileHeaderProps {
  user: UserModel;
  stats?: UserStatisticDto;
  fingerprints?: UserFingerprintDto[];
}

const MAX_VISIBLE_FINGERPRINTS = 3;

export const ProfileHeader: FC<ProfileHeaderProps> = ({ user, stats, fingerprints }) => {
  const [showAllFingerprints, setShowAllFingerprints] = useState(false);

  const allFingerprintIds = fingerprints?.map((fp) => fp.fingerprintId) ?? [];
  const hasMoreFingerprints = allFingerprintIds.length > MAX_VISIBLE_FINGERPRINTS;

  const visibleFingerprintIds = showAllFingerprints
    ? allFingerprintIds
    : allFingerprintIds.slice(0, MAX_VISIBLE_FINGERPRINTS);

  const fingerprintText = allFingerprintIds.length === 0 ? LONG_DASH : visibleFingerprintIds.join(', ');

  const registered =
    stats?.registrationDate != null && stats.registrationDate !== ''
      ? dayjs(stats.registrationDate).format('YYYY-MM-DD')
      : LONG_DASH;

  const emailDisplay = user.email != null && user.email !== '' ? user.email : LONG_DASH;
  const telegramDisplay =
    user.telegramUserName != null && user.telegramUserName !== '' ? user.telegramUserName : LONG_DASH;
  const ipDisplay = user.clientIP != null && user.clientIP !== '' ? user.clientIP : LONG_DASH;

  return (
    <Card className={css.profileHeader}>
      <div className={css.headerContent}>
        <UserAvatar className={css.profileAvatar} imageUrl={user.imgUrl} />

        <div className={css.profileInfo}>
          <div className={css.profileTitleRow}>
            <h1 className={css.profileName}>{user.userNickname}</h1>

            <div className={css.profileMarks}>
              {user.isNewUser && <Chip label="NEW" color="info" size="small" />}

              {user.levelVipPoints > 0 && (
                <Chip label="VIP" color="secondary" size="small" icon={<Crown width={14} height={14} />} />
              )}

              {user.isCheater && (
                <Chip label="Cheater" color="warning" size="small" icon={<Drama width={16} height={16} />} />
              )}

              {user.isBanned && (
                <Chip label="Banned" color="error" size="small" icon={<ShieldBan width={14} height={14} />} />
              )}
            </div>
          </div>

          <div className={css.profileDetails}>
            <div className={css.profileDetailItem}>
              <AtSign width={16} height={16} aria-hidden />

              <span>
                <span className={css.profileDetailLabel}>Telegram:</span> {telegramDisplay}
              </span>
            </div>

            <div className={css.profileDetailItem}>
              <Mail width={16} height={16} aria-hidden />

              <span>
                <span className={css.profileDetailLabel}>Email:</span> {emailDisplay}
              </span>
            </div>

            <div className={css.profileDetailItem}>
              <Globe width={16} height={16} aria-hidden />

              <span>
                <span className={css.profileDetailLabel}>IP:</span> {ipDisplay}
              </span>
            </div>

            <div className={css.profileDetailItem}>
              <Fingerprint width={16} height={16} aria-hidden />

              <span>
                <span className={css.profileDetailLabel}>Fingerprint:</span> {fingerprintText}
                {hasMoreFingerprints && (
                  <button
                    className={css.readMoreButton}
                    type="button"
                    onClick={() => setShowAllFingerprints((prev) => !prev)}
                  >
                    {showAllFingerprints ? 'Read less' : 'Read more'}
                  </button>
                )}
              </span>
            </div>

            <div className={css.profileDetailItem}>
              <MapPin width={16} height={16} aria-hidden />

              <span>
                <span className={css.profileDetailLabel}>Geo interface:</span>{' '}
                {geoToUIMap[user.geo] ? (
                  <Chip label={geoToUIMap[user.geo].label} color={geoToUIMap[user.geo].color} size="small" />
                ) : (
                  LONG_DASH
                )}
              </span>
            </div>

            <div className={css.profileDetailItem}>
              <Calendar width={16} height={16} aria-hidden />

              <span>
                <span className={css.profileDetailLabel}>Registered:</span> {registered}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};
